import {GMI, CAR_AND_DRIVER, STATE} from '../../constants';
import reservations from '../../services/cda/reservations';
import request from '../request';
import {types} from "../../utils/js/types";
import {batchActions} from "../../utils/redux";

const {INITIATE, INITIATE_PATH} = STATE.UI.CAR_AND_DRIVER;
const {POINT_TO_POINT} = CAR_AND_DRIVER.BOOKING_WIDGET;

/*************************** PRIVATE METHODS ***************************/

/**
 * @private
 * @name setLocationByIndex
 * @param {String} index
 * @param {Object} location data object
 * @returns {Function}
 */
function setLocationByIndex(index, location) {
  return index === 0 ? updatePickupLocation(location) : setStopByIndex((index - 1), location);
}

/**
 * @private
 * @name setLocationByUid
 * @param {String} uid
 * * @param {String} uidKey
 * @param {Object} location data object
 * @returns {Function}
 */
function setLocationByUid(uid, uidKey = 'uid', location) {
  return (dispatch, getState) => {
    const state = getState();
    const pickupUid = state.getIn([...INITIATE.PICKUP.LOCATION, uidKey]);
    if(uid === pickupUid) {
      dispatch(updatePickupLocation(location));
    } else {
      dispatch(setStopByUid(uid, uidKey, location));
    }
  };
}

/**
 * @private
 * @name setStops
 * @param {Array} stops
 * @returns {Object} {type: string, path: [], value: {}}
 */
function setStops (stops) {
  return {
    type: GMI.SET_GMI_STATE,
    path: INITIATE.EXTRA_STOPS,
    value: stops
  };
}

/**
 * @private
 * @name updatePickupLocation
 * @param {Object} location
 * @returns {Object} {type: string, path: [], value: {}}
 */
function updatePickupLocation (location = {}) {
  return {
    type: GMI.SET_GMI_STATE,
    path: INITIATE.PICKUP.LOCATION,
    value: location
  };
}

/**
 * @private
 * @name setStopByIndex
 * @param {String} index
 * @param {Object} location
 * @returns {Object} {type: string, path: [], value: {}}
 */
function setStopByIndex (index, location = {}) {
  return {
    type: GMI.SET_GMI_STATE,
    path: [...INITIATE.EXTRA_STOPS, index],
    value: location
  };
}

/**
 * @private
 * @name setStopByUid
 * @param {String} uid
 * @param {String} uidKey
 * @param {Object} location
 * @returns {Object} {type: string, path: [], value: {}}
 */
function setStopByUid (uid, uidKey, location = {}) {
  return {
    type: GMI.UPDATE_GMI_STATE_LIST_BY_UID,
    path: INITIATE.EXTRA_STOPS,
    uid,
    uidKey,
    value: location,
    replace: true,
    createNew: true
  };
}

/**
 * @private
 * @name setTimestamp
 * @param {String} timeStamp
 * @returns {Object} {type: string, path: [], value: string}
 */
function setTimestamp(timeStamp) {
  return {
    type: GMI.SET_GMI_STATE,
    path: INITIATE.PICKUP.PICKUP_DATE_TIME,
    value: timeStamp
  };
}

/**
 * @private
 * @name setRequestedHours
 * @param {String} duration
 * @returns {Object} {type: string, path: [], value: string}
 */
function setRequestedHours(duration) {
  return {
    type: GMI.SET_GMI_STATE,
    path: INITIATE.REQUESTED_DURATION,
    value: duration
  };
}

/**
 * @private
 * @name setPassengers
 * @param {Number} passengers
 * @returns {Object} {type: string, path: [], value: string}
 */
function setPassengers(passengers) {
  return {
    type: GMI.SET_GMI_STATE,
    path: INITIATE.PASSENGERS,
    value: passengers
  };
}

/**
 * @private
 * @name setBookingType
 * @param {String} bookingType
 * @returns {Object} {type: string, path: [], value: string}
 */
function setBookingType(bookingType) {
  return {
    type: GMI.SET_GMI_STATE,
    path: INITIATE.BOOKING_TYPE,
    value: bookingType
  };
}

/**
 * @private
 * @name removeStop
 * @param {Number} index
 * @returns {Object} {type: string, path: []}
 */
function removeStop(index) {
  return {
    type: GMI.DELETE_GMI_STATE,
    path: [...INITIATE.EXTRA_STOPS, index]
  };
}

/**
 * @private
 * @name clearLocations
 * @returns {Function}
 */
function clearLocations() {
  return batchActions(
    updatePickupLocation(),
    clearStops()
  );
}

/**
 * @private
 * @name clearStops
 * @returns {Object} {type: string, path: [], value: []}
 */
function clearStops() {
  return {
    type: GMI.SET_GMI_STATE,
    path: INITIATE.EXTRA_STOPS,
    value: []
  };
}

/**
 * @private
 * @desc Dispatch all relevant initiate actions from session object
 * @param {Object} args
 * @returns {Function}
 */
function setInitiateState(args = {}) {
  return (dispatch) => {
    const {
      dropoff,
      pickup,
      extra_stops,
      number_of_passengers = 1,
      requested_duration = 0,
      booking_type = POINT_TO_POINT
    } = args;
    const batchedActions = [];
    batchedActions.push(clearLocations());
    batchedActions.push(setRequestedHours(requested_duration));
    batchedActions.push(setBookingType(booking_type));
    if (pickup && pickup.pickup_date_time) batchedActions.push(setTimestamp(pickup.pickup_date_time));
    batchedActions.push(setPassengers(number_of_passengers));
    if (pickup && pickup.location) {
      batchedActions.push(updatePickupLocation(pickup.location));
    }
    if (dropoff && dropoff.location && !dropoff.location.as_directed) extra_stops.push(dropoff.location);
    if (extra_stops && extra_stops.length !== 0) extra_stops.forEach((stop, index) => batchedActions.push(setStopByIndex(index, stop)));
    if (batchedActions.length !== 0) dispatch(batchActions(...batchedActions));
  };
}

/**
 * Exported for test purposes
 * @private
 * @desc Set final stop as drop_off_location; remove it from stops, and set requested_duration by booking_type
 * @param {Object} payload
 * @returns {Object}
 */
export function prepareInitiatePayload(payload) {
  let dropoff = {};
  if (payload.extra_stops.length !== 0) dropoff = {as_directed:false, location: payload.extra_stops.pop()};
  if (!dropoff.location || !dropoff.location.latitude) {
    dropoff = {as_directed:true};
    payload.extra_stops = [];
  }
  Object.assign(payload, {dropoff});
  if (payload.booking_type === POINT_TO_POINT) payload.requested_duration = 0;
  return payload;
}


/*************************** PUBLIC METHODS ***************************/

const LOCATION = 'location';
const EXTRA_STOPS = 'extra_stops';
const CLEAR_LOCATIONS = 'clear_locations';
const PICKUP_TIMESTAMP = 'pickup_date_time';
const REQUESTED_DURATION = 'requested_duration';
const PASSENGERS = 'number_of_passengers';
const BOOKING_TYPE = 'booking_type';

export const initiateState = {
  /**
   * @name set
   * @desc A global setter for all carAndDriver.initiate payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.carAndDriver.initiateState
   * @example
   *
   * // set booking type
   * store.dispatch(actions.carAndDriver.initiateState.set('booking_type', {bookingType:'POINT_TO_POINT'}));
   *
   * // set location by index
   * store.dispatch(actions.carAndDriver.initiateState.set('location', {location: {...location model}, index: 0}));
   *
   * // set location by uid
   * store.dispatch(actions.carAndDriver.initiateState.set('location', {uid: "123", location: {...location model, uid: "123"}}));
   *
   * // set location by uid with alternate uidKey
   * store.dispatch(actions.carAndDriver.initiateState.set('location', {uid: "123", uidKey: "otherKey", location: {...location model, otherKey: "123"}}));
   *
   * // set stops
   * store.dispatch(actions.carAndDriver.initiateState.set('extra_stops', {stops: [ {location1}, {location2}] }));
   *
   * // set pickup time
   * store.dispatch(actions.carAndDriver.initiateState.set('pickup_date_time', {timestamp: '2019-01-22T12:00'}));
   *
   * // set requested hours
   * store.dispatch(actions.carAndDriver.initiateState.set('requested_duration', {duration: '4'}));
   *
   * // set passengers
   * store.dispatch(actions.carAndDriver.initiateState.set('number_of_passengers', {passengers: '4'}));
   *
   * // set all initiate state
   * store.dispatch(actions.carAndDriver.initiateState.set(null, {...}));
   */
  set (type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === LOCATION) {
          if(types(args.uid).isDefined) {
            dispatch(setLocationByUid(args.uid, args.uidKey, args.location));
          } else if (types(args.index).isDefined) {
            dispatch(setLocationByIndex(args.index, args.location));
          }
        } else if (type === EXTRA_STOPS) {
          dispatch(setStops(args.stops));
        } else if (type === PICKUP_TIMESTAMP) {
          dispatch(setTimestamp(args.timestamp));
        } else if (type === REQUESTED_DURATION) {
          dispatch(setRequestedHours(args.duration));
        } else if (type === PASSENGERS) {
          dispatch(setPassengers(args.passengers));
        } else if (type === BOOKING_TYPE) {
          dispatch(setBookingType(args.bookingType));
        } else {
          console.warn(`${type} is not a supported state type`);
        }
      } else {
        dispatch(setInitiateState(args));
      }
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for all carAndDriver.initiate payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.carAndDriver.initiateState
   * @example
   *
   * // unset location
   * store.dispatch(actions.carAndDriver.initiateState.unset('location', {index: 0}));
   *
   * // unset clear locations
   * store.dispatch(actions.carAndDriver.initiateState.unset('clear_locations'));
   */
  unset (type = null, args) {
    return dispatch => {
      if (types(type).isString) {
        if (type === LOCATION) {
          dispatch(removeStop(args.index));
        } else if (type === CLEAR_LOCATIONS) {
          dispatch(clearLocations());
        } else {
          console.warn(`${type} is not a supported state type`);
        }
      } else {
        //TODO: set up batched updates like in reservations.initiate.set
        console.warn(`carAndDriver.initiate batch updates not supported yet`);
      }
    };
  }
};

/**
 * @name initiate
 * @desc Make the CDA reservations/initiate call with state data;
 * @param {Object} [data] {params} Will get merged with getState().getIn(INITIATE).toJS() object
 * @param {Object} [requestOpts]
 * @returns {Function}
 * @memberof actions.carAndDriver
 * @example
 * store.dispatch(actions.carAndDriver.initiate());
 */
export function initiate(data, requestOpts = {}) {
  return (dispatch, getState) => {
    // transform payload to something deem/cda expects
    const payload = prepareInitiatePayload(getState().getIn(INITIATE_PATH).toJS());
    if (!types(data).isObject) data = {};
    Object.assign(data, {payload});
    return dispatch(request({
      data,
      service: reservations.initiate,
      opts: requestOpts
    }));
  };
}
